import { Check, Package } from 'lucide-react';
import { formatMoney } from '../../utils/format';

/**
 * PackageSelector - Radio button selector for package groups
 * Displays options like Basic/Premium/Elite with pricing
 */
export default function PackageSelector({
  packageGroup,
  options,
  selectedOption,
  onSelect,
  disabled = false
}) {
  if (!options || options.length === 0) {
    return null;
  }

  // Group options by package_option value
  const packageOptions = options.reduce((acc, item) => {
    if (!acc[item.package_option]) {
      acc[item.package_option] = {
        name: item.package_option,
        items: [],
        total: 0,
        isDefault: false
      };
    }
    acc[item.package_option].items.push(item);
    acc[item.package_option].total += parseFloat(item.unit_price) * item.quantity;
    if (item.is_default_selection) {
      acc[item.package_option].isDefault = true;
    }
    return acc;
  }, {});

  const sortedOptions = Object.values(packageOptions).sort((a, b) => a.total - b.total);

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Package className="w-5 h-5 text-primary-500" />
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">{packageGroup}</h3>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {sortedOptions.map((option) => {
          const isSelected = selectedOption === option.name;

          return (
            <button
              key={option.name}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(option.name)}
              className={`
                relative p-4 rounded-lg border-2 text-left transition-all
                ${isSelected
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-200 dark:ring-primary-800'
                  : 'border-neutral-200 dark:border-neutral-600 hover:border-neutral-300 dark:hover:border-neutral-500 bg-white dark:bg-neutral-800'
                }
                ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {/* Selection indicator */}
              <div className={`
                absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center
                ${isSelected ? 'bg-primary-500 text-white' : 'border-2 border-neutral-300 dark:border-neutral-500'}
              `}>
                {isSelected && <Check className="w-4 h-4" />}
              </div>

              {/* Option name */}
              <div className="mb-2">
                <span className="font-semibold text-neutral-900 dark:text-white">{option.name}</span>
                {option.isDefault && (
                  <span className="ml-2 text-xs bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded">
                    Recommended
                  </span>
                )}
              </div>

              {/* Price */}
              <div className="text-2xl font-bold text-neutral-900 dark:text-white mb-3">
                {formatMoney(option.total)}
              </div>

              {/* Items included */}
              <div className="space-y-1">
                {option.items.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-300">
                    <Check className="w-4 h-4 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <span>
                      {item.quantity > 1 && `${item.quantity}x `}
                      {item.product_name}
                    </span>
                  </div>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
